'use client'

import { useState } from 'react'
import { toast } from 'react-toastify'
import { MdHandshake, MdKey, MdSave } from 'react-icons/md'
import { connectMetaPartner, connectMetaPixelToken, MetaConnectionSummary } from '@/lib/api/marketing/meta-api'

type DirectMode = 'PIXEL_TOKEN' | 'PARTNER'

interface MetaDirectConnectionFormProps {
    /** Conexão atual (edição ou reconexão); vazio para conectar pela primeira vez. */
    connection?: MetaConnectionSummary | null
    onSaved: (connection: MetaConnectionSummary) => void
}

const MODE_OPTIONS: { value: DirectMode; label: string; description: string; icon: typeof MdKey }[] = [
    {
        value: 'PIXEL_TOKEN',
        label: 'Token do pixel',
        description: 'Gere o token no Gerenciador de Eventos do pixel e cole aqui. Funciona para qualquer empresa.',
        icon: MdKey,
    },
    {
        value: 'PARTNER',
        label: 'Compartilhar com a plataforma',
        description:
            'Atribua a plataforma como parceira do pixel e informe só o ID. A Meta só libera para empresas com algumas semanas de uso.',
        icon: MdHandshake,
    },
]

/**
 * Conexão sem login na Meta: só envio de eventos de conversão (CAPI). O pixel
 * é da empresa; o acesso vem do token gerado por ela no Gerenciador de Eventos
 * ou do compartilhamento como parceiro com a plataforma.
 */
export default function MetaDirectConnectionForm({ connection, onSaved }: MetaDirectConnectionFormProps) {
    const currentMode =
        connection?.authMode === 'PARTNER' || connection?.authMode === 'PIXEL_TOKEN' ? connection.authMode : null
    const isEditing = !!currentMode && connection?.status === 'ACTIVE'

    // Conexão sem login desconectada ainda preenche o formulário, para reconectar rápido
    const [mode, setMode] = useState<DirectMode>(currentMode ?? 'PIXEL_TOKEN')
    const [pixelId, setPixelId] = useState(currentMode ? connection?.pixelId || '' : '')
    const [accessToken, setAccessToken] = useState('')
    const [testEventCode, setTestEventCode] = useState(currentMode ? connection?.capiTestEventCode || '' : '')
    const [saving, setSaving] = useState(false)

    // O token gravado nunca volta para a tela: na edição do mesmo modo, vazio mantém o atual
    const keepsStoredToken = isEditing && currentMode === 'PIXEL_TOKEN' && mode === 'PIXEL_TOKEN'

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!pixelId.trim()) {
            toast.error('Informe o ID do pixel')
            return
        }
        if (mode === 'PIXEL_TOKEN' && !accessToken.trim() && !keepsStoredToken) {
            toast.error('Informe o token de acesso')
            return
        }
        setSaving(true)
        try {
            const capiTestEventCode = testEventCode.trim() || null
            const res =
                mode === 'PIXEL_TOKEN'
                    ? await connectMetaPixelToken({
                          pixelId: pixelId.trim(),
                          accessToken: accessToken.trim() || null,
                          capiTestEventCode,
                      })
                    : await connectMetaPartner({ pixelId: pixelId.trim(), capiTestEventCode })
            if (res.success) {
                toast.success(isEditing ? 'Configuração do pixel atualizada' : 'Pixel conectado')
                setAccessToken('')
                onSaved(res.result)
            } else {
                toast.error(res.error || 'Não foi possível conectar o pixel')
            }
        } finally {
            setSaving(false)
        }
    }

    return (
        <form
            onSubmit={handleSubmit}
            className="bg-base-100 rounded-2xl border border-base-300/50 shadow-sm p-6 space-y-5"
        >
            <div>
                <h3 className="font-semibold text-base-content">
                    {isEditing ? 'Pixel conectado sem login' : 'Conectar sem login na Meta'}
                </h3>
                <p className="text-sm text-base-content/60 mt-1">
                    Envia os eventos de conversão (CAPI) para o pixel da empresa. Não inclui captação de Lead Ads nem
                    Instagram Direct e Messenger.
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {MODE_OPTIONS.map(option => {
                    const Icon = option.icon
                    const selected = mode === option.value
                    return (
                        <label
                            key={option.value}
                            className={`flex items-start gap-3 rounded-xl border p-4 cursor-pointer transition-colors ${
                                selected
                                    ? 'border-secondary bg-secondary/5'
                                    : 'border-base-300/70 hover:border-base-content/30'
                            }`}
                        >
                            <input
                                type="radio"
                                name="meta-direct-mode"
                                className="radio radio-secondary radio-sm mt-0.5"
                                checked={selected}
                                onChange={() => setMode(option.value)}
                                disabled={saving}
                            />
                            <div>
                                <span className="flex items-center gap-1.5 text-sm font-medium text-base-content">
                                    <Icon className="text-base text-secondary" /> {option.label}
                                </span>
                                <span className="block text-xs text-base-content/60 mt-1">{option.description}</span>
                            </div>
                        </label>
                    )
                })}
            </div>

            {!isEditing && (
                <ol className="list-decimal list-inside space-y-1.5 text-sm text-base-content/70 bg-base-200/50 rounded-xl p-4">
                    {mode === 'PIXEL_TOKEN' ? (
                        <>
                            <li>No Gerenciador de Eventos da Meta, abra o pixel e vá em Configurações.</li>
                            <li>Na seção API de Conversões, clique em Gerar token de acesso.</li>
                            <li>Copie o token e cole abaixo, junto com o ID do pixel.</li>
                        </>
                    ) : (
                        <>
                            <li>
                                No Gerenciador de Negócios da Meta, abra Configurações do negócio &gt; Fontes de dados
                                &gt; Conjuntos de dados e pixels.
                            </li>
                            <li>
                                Selecione o pixel e use Atribuir parceiro, informando o ID da empresa da plataforma
                                fornecido pelo suporte.
                            </li>
                            <li>Conceda a permissão de gerenciar o conjunto de dados e cole abaixo o ID do pixel.</li>
                        </>
                    )}
                </ol>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="form-control w-full">
                    <label className="label py-1" htmlFor="meta-direct-pixel">
                        <span className="label-text text-sm font-medium">ID do pixel</span>
                    </label>
                    <input
                        id="meta-direct-pixel"
                        type="text"
                        inputMode="numeric"
                        className="input input-bordered w-full"
                        placeholder="Ex.: 123456789012345"
                        value={pixelId}
                        onChange={e => setPixelId(e.target.value.replace(/\D/g, ''))}
                        disabled={saving}
                    />
                </div>

                <div className="form-control w-full">
                    <label className="label py-1" htmlFor="meta-direct-test-code">
                        <span className="label-text text-sm font-medium">Código de teste (opcional)</span>
                    </label>
                    <input
                        id="meta-direct-test-code"
                        type="text"
                        className="input input-bordered w-full"
                        placeholder="Ex.: TEST12345"
                        value={testEventCode}
                        onChange={e => setTestEventCode(e.target.value)}
                        disabled={saving}
                    />
                    <span className="label-text-alt text-base-content/50 mt-1">
                        Com o código preenchido, todos os eventos aparecem só em Eventos de teste. Apague para medir de
                        verdade.
                    </span>
                </div>

                {mode === 'PIXEL_TOKEN' && (
                    <div className="form-control w-full md:col-span-2">
                        <label className="label py-1" htmlFor="meta-direct-token">
                            <span className="label-text text-sm font-medium">Token de acesso</span>
                        </label>
                        <textarea
                            id="meta-direct-token"
                            className="textarea textarea-bordered w-full font-mono text-xs"
                            rows={3}
                            autoComplete="off"
                            spellCheck={false}
                            placeholder={
                                keepsStoredToken
                                    ? 'Deixe em branco para manter o token atual'
                                    : 'Cole aqui o token gerado'
                            }
                            value={accessToken}
                            onChange={e => setAccessToken(e.target.value)}
                            disabled={saving}
                        />
                        <span className="label-text-alt text-base-content/50 mt-1">
                            O token é guardado criptografado e não é exibido de novo.
                        </span>
                    </div>
                )}
            </div>

            <div className="flex justify-end">
                <button type="submit" className="btn btn-secondary gap-2" disabled={saving}>
                    {saving ? <span className="loading loading-spinner loading-sm" /> : <MdSave className="text-lg" />}
                    {isEditing ? 'Salvar' : 'Conectar pixel'}
                </button>
            </div>
        </form>
    )
}
